const DEDUPE_WINDOW_MS = 5000;
const MAX_DEDUPE_ENTRIES = 100;
const MAX_MESSAGE_LENGTH = 1000;
const MAX_STACK_LENGTH = 10000;

const recentErrors = new Map();

const sanitizeText = (value = '') => String(value)
  .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
  .replace(/\+?\d[\d\s().-]{7,}\d/g, '[phone]')
  .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[jwt]')
  .replace(/\b[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[token]');

const limitText = (value, maxLength) => {
  const text = sanitizeText(value);
  return text.length > maxLength ? text.slice(0, maxLength) : text;
};

const getStoredUser = () => {
  try {
    const rawUser = localStorage.getItem('User') || sessionStorage.getItem('User');
    return rawUser ? JSON.parse(rawUser) : null;
  } catch (error) {
    return null;
  }
};

const getAccessToken = () => localStorage.getItem('accessToken');

const getRoute = () => {
  if (!window.location) {
    return 'unknown';
  }

  return window.location.pathname || '/';
};

const getBaseUrl = () => (process.env.NODE_ENV === 'development'
  ? 'http://localhost:8080/api'
  : 'https://chat.telein.com.br/api');

const normalizeError = (error) => {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === 'string' ? error : 'Erro JavaScript desconhecido');
};

const buildDedupeKey = (entry) => [
  entry.source,
  entry.area,
  entry.message,
  entry.componentStack,
].join('|');

const shouldReport = (entry) => {
  const now = Date.now();
  const key = buildDedupeKey(entry);
  const lastReportedAt = recentErrors.get(key);

  if (lastReportedAt && now - lastReportedAt < DEDUPE_WINDOW_MS) {
    return false;
  }

  recentErrors.set(key, now);

  if (recentErrors.size > MAX_DEDUPE_ENTRIES) {
    const firstKey = recentErrors.keys().next().value;
    recentErrors.delete(firstKey);
  }

  return true;
};

export const reportClientError = (error, metadata = {}) => {
  try {
    const normalizedError = normalizeError(error);
    const user = getStoredUser();
    const accessToken = getAccessToken();
    const entry = {
      area: metadata.area || 'application',
      componentStack: limitText(metadata.componentStack || '', MAX_STACK_LENGTH),
      environmentId: user?.ambiente ?? null,
      message: limitText(normalizedError.message || 'Erro JavaScript desconhecido', MAX_MESSAGE_LENGTH),
      route: getRoute(),
      severity: metadata.severity || 'error',
      source: metadata.source || 'javascript',
      stack: limitText(normalizedError.stack || '', MAX_STACK_LENGTH),
      timestamp: new Date().toISOString(),
      userId: user?.id ?? null,
      version: process.env.REACT_APP_VERSION || 'unknown',
    };

    if (!shouldReport(entry)) {
      return;
    }

    const reportingEnabled = process.env.REACT_APP_OBSERVABILITY_CLIENT_ERRORS_ENABLED === 'true';

    if (reportingEnabled && accessToken) {
      fetch(`${getBaseUrl()}/observability/client-errors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...(user?.id && { 'X-User-ID': user.id }),
        },
        credentials: 'include',
        body: JSON.stringify(entry),
      }).catch((sendError) => {
        // eslint-disable-next-line no-console
        console.error('[client-error-reporter-failed]', sendError);
      });
      return;
    }

    // eslint-disable-next-line no-console
    console.error('[client-error]', entry);
  } catch (reporterError) {
    // eslint-disable-next-line no-console
    console.error('[client-error-reporter-failed]', reporterError);
  }
};

export const resetClientErrorReporterForTests = () => {
  recentErrors.clear();
};
